import type { Revision } from '../../types';
import { mutations } from '../../utils';
import { isRevisionPageDocument } from './guards';
import { removePageInRevision } from './removePageInRevision';
import { restorePageInRevision } from './restorePageInRevision';
import { updatePageInRevision } from './updatePageInRevision';

/**
 * Which structural change {@link revertPageToRevision} applied.
 */
export type RevertPageToRevisionKind = 'removed' | 'restored' | 'reverted-content' | 'noop';

/**
 * Revert one page to the state it had in `targetRevision`, mutating `revision` in place. Relative to
 * the target:
 * - present now, absent in target → the page was added since → remove it (`removed`).
 * - absent now, present in target → the page was deleted since → restore it (`restored`);
 *   `restorePageInRevision` throws if the page isn't a restorable subtree root (parent also gone).
 * - present in both → revert its **content** by pointing it back at the target's document
 *   (`reverted-content`). Page properties/metadata (title, slug, tags, …) are intentionally left
 *   untouched — the body diff a user reverts is content-only.
 *
 * Only mutates the revision's page pointer — a caller editing a live document (e.g. an open editor)
 * is responsible for rewriting that document separately so it reflects the revert immediately.
 */
export function revertPageToRevision(
    revision: mutations.MutableData<Revision>,
    input: { targetRevision: Revision; pageId: string }
): { kind: RevertPageToRevisionKind } {
    const { targetRevision, pageId } = input;
    const currentPage = mutations.getIn(revision, ['pages', pageId] as const);
    const targetPage = targetRevision.pages[pageId];

    if (currentPage && !targetPage) {
        removePageInRevision(revision, pageId);
        return { kind: 'removed' };
    }

    if (!currentPage && targetPage) {
        if (!isRevisionPageDocument(targetPage)) {
            return { kind: 'noop' };
        }
        restorePageInRevision(revision, structuredClone(targetPage), {
            sourceRevision: targetRevision,
        });
        return { kind: 'restored' };
    }

    if (currentPage && targetPage && isRevisionPageDocument(targetPage)) {
        updatePageInRevision(revision, pageId, { document: targetPage.document });
        return { kind: 'reverted-content' };
    }

    return { kind: 'noop' };
}
